import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table, Button, Form, Input, InputNumber, Select, DatePicker, Space, Card,
  message, Typography, Modal, Switch, Segmented, Alert, Tabs, Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined, UserAddOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import { useRolePreview } from '../../contexts/RolePreviewContext';
import AttachExistingUsersModal from '../../components/workspace/AttachExistingUsersModal';
import {
  listGroupInstances, getGroupInstance, createGroupInstance, updateGroupInstance, deleteGroupInstance,
  addGroupMember, removeGroupMember, removeGroupMembersByUserIds, resolveGroupPolicy,
  listPackages, listControlTypes, listNfsLocations, listGroupTypes, listIwmProfiles, IwmProfile,
  GroupInstance, GroupMember, WorkspacePackage, ControlType, NfsLocation, GroupType,
} from '../../services/workspaceApi';
import { getInfraServers } from '../../services/api';

interface InfraServerOption {
  id: number;
  hostName: string;
  port: number;
}

type SetupMode = 'batch' | 'individual';
type DetailTab = 'members' | 'settings';

const BATCH_SETUP_CODES = ['ws', 'po', 'up', 'ti'] as const;
const INDIVIDUAL_SETUP_CODES = ['ts', 'el', 'cg', 'tg'] as const;

/** Top pane height % when detail is open (list). Persisted across visits. */
const SPLIT_STORAGE_KEY = 'ic.groupInstances.splitTopPct';
const SPLIT_DEFAULT_TOP_PCT = 30;
const SPLIT_MIN_TOP_PCT = 18;
const SPLIT_MAX_TOP_PCT = 55;

const readStoredSplitPct = (): number => {
  try {
    const raw = localStorage.getItem(SPLIT_STORAGE_KEY);
    const n = raw != null ? Number(raw) : NaN;
    if (Number.isFinite(n) && n >= SPLIT_MIN_TOP_PCT && n <= SPLIT_MAX_TOP_PCT) return n;
  } catch {
    /* ignore */
  }
  return SPLIT_DEFAULT_TOP_PCT;
};

const resolveGroupType = (row: GroupInstance, groupTypes: GroupType[]): GroupType | undefined =>
  row.groupType ?? groupTypes.find((g) => g.id === row.groupTypeId);

const isBatchSetup = (gt: GroupType | undefined): boolean => {
  if (!gt) return false;
  const code = gt.code.toLowerCase();
  const kind = gt.kind.toLowerCase();
  return kind === 'batch' || (BATCH_SETUP_CODES as readonly string[]).includes(code);
};

const isIndividualSetup = (gt: GroupType | undefined): boolean => {
  if (!gt) return false;
  const code = gt.code.toLowerCase();
  const kind = gt.kind.toLowerCase();
  return kind === 'individual' || kind === 'trainer' || (INDIVIDUAL_SETUP_CODES as readonly string[]).includes(code);
};

const matchesSetupMode = (row: GroupInstance, mode: SetupMode, groupTypes: GroupType[]): boolean => {
  const gt = resolveGroupType(row, groupTypes);
  return mode === 'batch' ? isBatchSetup(gt) : isIndividualSetup(gt);
};

const filterGroupTypesForMode = (types: GroupType[], mode: SetupMode): GroupType[] =>
  types.filter((g) => (mode === 'batch' ? isBatchSetup(g) : isIndividualSetup(g)));

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

const GroupInstancesPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const { canWriteWorkspace: canWrite } = useRolePreview();

  const [setupMode, setSetupMode] = useState<SetupMode>('batch');
  const [rows, setRows] = useState<GroupInstance[]>([]);
  const [packages, setPackages] = useState<WorkspacePackage[]>([]);
  const [controlTypes, setControlTypes] = useState<ControlType[]>([]);
  const [groupTypes, setGroupTypes] = useState<GroupType[]>([]);
  const [nfsLocations, setNfsLocations] = useState<NfsLocation[]>([]);
  const [infraServers, setInfraServers] = useState<InfraServerOption[]>([]);
  const [iwmProfiles, setIwmProfiles] = useState<IwmProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<GroupInstance | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [form] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [attachExistingOpen, setAttachExistingOpen] = useState(false);
  const [memberSelectedKeys, setMemberSelectedKeys] = useState<React.Key[]>([]);
  const [removingMembers, setRemovingMembers] = useState(false);
  const [memberFilter, setMemberFilter] = useState('');
  const [detailTab, setDetailTab] = useState<DetailTab>('settings');
  const [listTopPct, setListTopPct] = useState(readStoredSplitPct);
  const listTopPctRef = useRef(listTopPct);
  listTopPctRef.current = listTopPct;
  const splitDragRef = useRef<{ startY: number; startPct: number } | null>(null);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  const selectedGroupTypeId = Form.useWatch('groupTypeId', form);
  const watchedName = Form.useWatch('name', form);
  const selectedGroupType = useMemo(
    () => groupTypes.find((g) => g.id === selectedGroupTypeId),
    [groupTypes, selectedGroupTypeId],
  );
  const isPoGroupType = selectedGroupType?.code.toLowerCase() === 'po';

  const filteredGroupTypes = useMemo(
    () => filterGroupTypesForMode(groupTypes, setupMode),
    [groupTypes, setupMode],
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesSetupMode(row, setupMode, groupTypes)),
    [rows, setupMode, groupTypes],
  );

  const loadLookups = useCallback(async () => {
    try {
      const [pkg, ct, nfs, gt, servers, profiles] = await Promise.all([
        listPackages(),
        listControlTypes(),
        listNfsLocations(),
        listGroupTypes(),
        getInfraServers(),
        listIwmProfiles().catch(() => [] as IwmProfile[]),
      ]);
      setPackages(Array.isArray(pkg) ? pkg : []);
      setControlTypes(Array.isArray(ct) ? ct : []);
      setNfsLocations(Array.isArray(nfs) ? nfs : []);
      setGroupTypes(Array.isArray(gt) ? gt : []);
      setInfraServers(Array.isArray(servers) ? servers : []);
      setIwmProfiles(Array.isArray(profiles) ? profiles : []);
    } catch {
      /* lookups optional for list view */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listGroupInstances();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load group instances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadLookups();
  }, [load, loadLookups]);

  const nfsByRole = (roleName: string) =>
    nfsLocations.filter((n) => n.role === roleName).map((n) => ({ value: n.id, label: `${n.code} (${n.mountPath})` }));

  const infraServerOptions = infraServers.map((s) => ({ value: s.id, label: `${s.hostName}:${s.port}` }));

  const openCreate = () => {
    setEditing(null);
    setMembers([]);
    form.resetFields();
    form.setFieldsValue({ status: 'draft' });
    setDetailTab('settings');
    setDrawerOpen(true);
  };

  const openEdit = async (record: GroupInstance) => {
    try {
      const full = await getGroupInstance(record.id);
      setEditing(full);
      setMembers(full.members || []);
      // affinityServerIds itself is write-only (converted server-side to
      // affinityServerIdsJson) — reads only ever return the JSON column, so
      // it has to be parsed back out here or the field always shows empty
      // on edit regardless of what was actually saved.
      let affinityServerIds: number[] = [];
      if (full.affinityServerIdsJson) {
        try {
          const parsed = JSON.parse(full.affinityServerIdsJson);
          if (Array.isArray(parsed)) affinityServerIds = parsed;
        } catch {
          /* leave empty if malformed */
        }
      }
      setMemberSelectedKeys([]);
      form.setFieldsValue({
        ...full,
        affinityServerIds,
        startDate: full.startDate ? dayjs(full.startDate) : null,
        endDate: full.endDate ? dayjs(full.endDate) : null,
      });
      setDetailTab('members');
      setDrawerOpen(true);
    } catch (e) {
      message.error(errMsg(e, 'Failed to load group') || 'Failed to load group');
    }
  };

  const closeEditor = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
    setMembers([]);
    setMemberSelectedKeys([]);
    setMemberFilter('');
    setDetailTab('settings');
  };

  const persistSplitPct = useCallback((pct: number) => {
    const clamped = Math.min(SPLIT_MAX_TOP_PCT, Math.max(SPLIT_MIN_TOP_PCT, pct));
    setListTopPct(clamped);
    try {
      localStorage.setItem(SPLIT_STORAGE_KEY, String(Math.round(clamped)));
    } catch {
      /* ignore */
    }
  }, []);

  const onSplitPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    splitDragRef.current = { startY: e.clientY, startPct: listTopPct };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onSplitPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!splitDragRef.current || !splitContainerRef.current) return;
    const height = splitContainerRef.current.getBoundingClientRect().height;
    if (height < 80) return;
    const deltaPct = ((e.clientY - splitDragRef.current.startY) / height) * 100;
    const next = Math.min(
      SPLIT_MAX_TOP_PCT,
      Math.max(SPLIT_MIN_TOP_PCT, splitDragRef.current.startPct + deltaPct),
    );
    setListTopPct(next);
  };

  const onSplitPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (splitDragRef.current) {
      try {
        localStorage.setItem(SPLIT_STORAGE_KEY, String(Math.round(listTopPctRef.current)));
      } catch {
        /* ignore */
      }
    }
    splitDragRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const resetSplit = () => persistSplitPct(SPLIT_DEFAULT_TOP_PCT);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const affinityRaw = values.affinityServerIds;
      const affinityIds =
        typeof affinityRaw === 'string'
          ? affinityRaw.split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !Number.isNaN(n))
          : Array.isArray(affinityRaw)
            ? affinityRaw
            : undefined;
      const payload: Partial<GroupInstance> & Record<string, unknown> = {
        ...values,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
      };
      (['toolsNfsLocationId', 'labNfsLocationId', 'pdkNfsLocationId', 'packageId', 'controlTypeId'] as const).forEach((key) => {
        const v = payload[key];
        payload[key] = v === undefined || v === '' ? null : (v as number);
      });
      if (affinityIds) {
        payload.affinityServerIds = affinityIds as any;
      }
      if (values.scheduledHours != null && typeof values.scheduledHours === 'number') {
        payload.scheduledHours = { weekly: values.scheduledHours } as any;
      }
      if (editing) {
        await updateGroupInstance(editing.id, payload);
        message.success('Updated');
        closeEditor();
      } else {
        const created = await createGroupInstance(payload);
        message.success('Created');
        if (setupMode === 'individual') {
          const full = await getGroupInstance(created.id);
          setEditing(full);
          setMembers(full.members || []);
          form.setFieldsValue({
            ...full,
            startDate: full.startDate ? dayjs(full.startDate) : null,
            endDate: full.endDate ? dayjs(full.endDate) : null,
          });
          setDetailTab('members');
        } else {
          closeEditor();
        }
      }
      await load();
    } catch (e) {
      const m = errMsg(e, 'Save failed');
      if (m) message.error(m);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record: GroupInstance) => {
    Modal.confirm({
      title: 'Delete group instance?',
      content: `Remove group #${record.id}${record.name ? ` (${record.name})` : ''}?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteGroupInstance(record.id);
          message.success('Deleted');
          await load();
        } catch (e) {
          message.error(errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const handleResolve = async (record: GroupInstance) => {
    setResolving(true);
    try {
      const result = await resolveGroupPolicy(record.id);
      Modal.info({
        title: 'Resolved Policy',
        width: 640,
        content: (
          <pre className="text-xs overflow-auto max-h-96 bg-gray-50 p-2 rounded">
            {JSON.stringify(result, null, 2)}
          </pre>
        ),
      });
    } catch (e) {
      message.error(errMsg(e, 'Resolve policy failed') || 'Resolve policy failed');
    } finally {
      setResolving(false);
    }
  };

  const handleAddMember = async () => {
    if (!editing) return;
    try {
      const values = await memberForm.validateFields();
      setAddingMember(true);
      const created = await addGroupMember(editing.id, values);
      // Normalize API shapes: single member | member[] | { members }
      let added: GroupMember[] = [];
      if (Array.isArray(created)) {
        added = created;
      } else if (created && typeof created === 'object') {
        const maybe = created as GroupMember & { members?: GroupMember[] };
        if (Array.isArray(maybe.members)) added = maybe.members;
        else if (maybe.userId) added = [maybe];
      }
      setMembers((prev) => {
        const byKey = new Map<string, GroupMember>();
        for (const m of prev) {
          const key = m.id != null ? `id:${m.id}` : `u:${m.userId}`;
          byKey.set(key, m);
        }
        for (const m of added) {
          if (!m?.userId && m?.id == null) continue;
          const key = m.id != null ? `id:${m.id}` : `u:${m.userId}`;
          byKey.set(key, { ...byKey.get(key), ...m });
        }
        return Array.from(byKey.values());
      });
      message.success('Member added');
      setMemberModalOpen(false);
      memberForm.resetFields();
      try {
        const full = await getGroupInstance(editing.id);
        setMembers(full.members || []);
      } catch {
        /* keep optimistic list */
      }
    } catch (e) {
      const m = errMsg(e, 'Add member failed');
      if (m) message.error(m);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = (member: GroupMember) => {
    if (!editing || member.id == null) return;
    Modal.confirm({
      title: 'Remove member?',
      content: `Remove ${member.userId}?`,
      okType: 'danger',
      onOk: async () => {
        try {
          await removeGroupMember(editing.id, member.id!);
          setMembers((prev) => prev.filter((m) => m.id !== member.id));
          setMemberSelectedKeys((keys) => keys.filter((k) => k !== String(member.id ?? member.userId)));
          message.success('Removed');
          await load();
        } catch (e) {
          message.error(errMsg(e, 'Remove failed') || 'Remove failed');
        }
      },
    });
  };

  const handleMakePrimary = async (member: GroupMember) => {
    if (!editing) return;
    try {
      // addGroupMember upserts on (groupInstanceId, userId) server-side —
      // no separate "update member" endpoint needed.
      const result = await addGroupMember(editing.id, { userId: member.userId, isPrimary: true });
      const updated: GroupMember | undefined = Array.isArray(result)
        ? result.find((m) => m.userId === member.userId)
        : result && typeof result === 'object' && 'members' in result
          ? (result as { members: GroupMember[] }).members.find((m) => m.userId === member.userId)
          : (result as GroupMember | undefined);
      setMembers((prev) =>
        prev.map((m) => (m.userId === member.userId ? { ...m, isPrimary: true, ...updated } : m)),
      );
      message.success(`${member.userId} marked primary`);
    } catch (e) {
      message.error(errMsg(e, 'Failed to set primary') || 'Failed to set primary');
    }
  };

  const handleBulkRemoveMembers = () => {
    if (!editing || memberSelectedKeys.length === 0) return;
    const selected = members.filter((m) =>
      memberSelectedKeys.includes(String(m.id ?? m.userId)),
    );
    if (selected.length === 0) return;
    Modal.confirm({
      title: `Remove ${selected.length} member(s)?`,
      content: (
        <div>
          <p>These users will leave group <strong>{editing.name || `#${editing.id}`}</strong> (membership only — Unique Users stay in the system):</p>
          <ul className="text-sm max-h-40 overflow-auto pl-4">
            {selected.slice(0, 40).map((m) => (
              <li key={m.userId}>{m.userId}</li>
            ))}
            {selected.length > 40 ? <li>…and {selected.length - 40} more</li> : null}
          </ul>
        </div>
      ),
      okText: 'Remove',
      okType: 'danger',
      onOk: async () => {
        setRemovingMembers(true);
        try {
          await removeGroupMembersByUserIds(
            editing.id,
            selected.map((m) => m.userId),
          );
          const removedIds = new Set(selected.map((m) => m.userId));
          setMembers((prev) => prev.filter((m) => !removedIds.has(m.userId)));
          setMemberSelectedKeys([]);
          message.success(`Removed ${selected.length} member(s)`);
          await load();
        } catch (e) {
          message.error(errMsg(e, 'Bulk remove failed') || 'Bulk remove failed');
        } finally {
          setRemovingMembers(false);
        }
      },
    });
  };

  const pkgLabel = (id?: number | null) => {
    if (id == null) return '—';
    const p = packages.find((x) => x.id === id);
    return p ? `${p.code}` : String(id);
  };

  const pkgProfile = (packageId?: number | null, override?: string | null) => {
    const inherited = packageId != null ? packages.find((p) => p.id === packageId)?.iwmProfileId : undefined;
    return (override || '').trim() || inherited || '—';
  };

  const watchedPackageId = Form.useWatch('packageId', form);
  const inheritedProfile = packages.find((p) => p.id === watchedPackageId)?.iwmProfileId;

  const groupTypeLabel = (record: GroupInstance) => {
    const gt = resolveGroupType(record, groupTypes);
    return gt ? `${gt.kind}/${gt.code}` : '—';
  };

  const newGroupLabel = setupMode === 'batch' ? 'New Batch Group' : 'New Individual Group';
  const panelTitleName =
    (typeof watchedName === 'string' && watchedName.trim()) ||
    editing?.name ||
    (setupMode === 'batch' ? 'New Batch Group' : 'New Individual Group');
  const panelSubtitle = editing
    ? `ID ${editing.id} · ${setupMode === 'batch' ? 'Batch / Group' : 'Individual'}`
    : 'Create — fill details then Save';

  const pagePurpose =
    'Two setup paths: Batch/Group (ws, po, up, ti) for shared package, control, NFS, and affinity; Individual (ts, el, cg, tg) for single-member allocations — add members after save.';

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 90 },
    {
      title: 'Group Type',
      key: 'groupType',
      width: 120,
      render: (_: unknown, record: GroupInstance) => groupTypeLabel(record),
    },
    {
      title: 'Package',
      dataIndex: 'packageId',
      key: 'packageId',
      width: 120,
      render: (id: number) => pkgLabel(id),
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 110,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 110,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'Profile',
      key: 'profile',
      width: 180,
      ellipsis: true,
      render: (_: unknown, record: GroupInstance) => pkgProfile(record.packageId, record.profileId),
    },
    {
      title: 'Members',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 90,
      render: (n: number | undefined) => (n != null ? n : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: canWrite ? 260 : 100,
      render: (_: unknown, record: GroupInstance) => (
        <Space wrap onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            {canWrite ? 'Edit' : 'View'}
          </Button>
          {canWrite && (
            <>
              <Button type="link" size="small" icon={<ThunderboltOutlined />} loading={resolving} onClick={() => handleResolve(record)}>
                Resolve Policy
              </Button>
              <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                Delete
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const memberCols = [
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: 80,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    canWrite && editing
      ? {
          title: '',
          key: 'actions',
          width: 160,
          render: (_: unknown, record: GroupMember) => (
            <Space size="small">
              {!record.isPrimary && (
                <Button type="link" size="small" onClick={() => handleMakePrimary(record)}>
                  Make Primary
                </Button>
              )}
              <Button type="link" danger size="small" onClick={() => handleRemoveMember(record)}>
                Remove
              </Button>
            </Space>
          ),
        }
      : null,
  ].filter(Boolean);

  const showMembersSection = Boolean(editing);

  const filteredMembers = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.userId.toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q),
    );
  }, [members, memberFilter]);

  const settingsForm = (
    <Form form={form} layout="vertical" disabled={!canWrite} className="max-w-4xl">
      {setupMode === 'individual' && !editing && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message="Individual setup"
          description="Save the group first — then switch to Members to add the allocation."
        />
      )}
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'Required' }]}
        extra={
          setupMode === 'batch' && isPoGroupType
            ? 'PO naming pattern: b<BatchNumber><GeoCode>_<year> — e.g. b23n_2025 (batch 23, Noida, 2025)'
            : setupMode === 'batch'
              ? 'Batch instance name (PO groups use b23n_2025 style)'
              : undefined
        }
      >
        <Input placeholder={setupMode === 'batch' && isPoGroupType ? 'b23n_2025' : undefined} />
      </Form.Item>
      <Form.Item name="groupTypeId" label="Group Type" rules={[{ required: true, message: 'Required' }]}>
        <Select
          options={filteredGroupTypes.map((g) => ({
            value: g.id,
            label: `${g.kind}/${g.code} — ${g.displayName}`,
          }))}
          showSearch
          optionFilterProp="label"
          placeholder={setupMode === 'individual' ? 'Individual or trainer type' : 'Batch group type'}
        />
      </Form.Item>
      <Space className="w-full" size="middle">
        <Form.Item name="startDate" label="Start" className="flex-1">
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item name="endDate" label="End" className="flex-1">
          <DatePicker className="w-full" />
        </Form.Item>
      </Space>
      <Form.Item name="packageId" label="Package">
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          options={packages.map((p) => ({ value: p.id, label: `${p.code} — ${p.displayName}` }))}
        />
      </Form.Item>
      <Form.Item name="controlTypeId" label="Control Type">
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          options={controlTypes.map((c) => ({ value: c.id, label: `${c.code} — ${c.displayName}` }))}
        />
      </Form.Item>
      <Form.Item name="toolsNfsLocationId" label="Tools NFS">
        <Select allowClear options={nfsByRole('tools')} />
      </Form.Item>
      <Form.Item name="labNfsLocationId" label="Labs NFS">
        <Select allowClear options={nfsByRole('labs')} />
      </Form.Item>
      <Form.Item name="pdkNfsLocationId" label="PDK NFS">
        <Select allowClear options={nfsByRole('pdk')} />
      </Form.Item>
      <Form.Item
        name="affinityServerIds"
        label="Affinity"
        extra="Servers this batch is pinned to for placement (see Server Info for the full list)"
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Select one or more servers"
          options={infraServerOptions}
          optionFilterProp="label"
        />
      </Form.Item>
      <Space className="w-full" size="middle">
        <Form.Item name="scheduledHours" label="Scheduled Hours" className="flex-1">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="maxWeekHours" label="Max Week Hours" className="flex-1">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Space>
      <Form.Item
        name="ttlMinutes"
        label="Session TTL (minutes)"
        extra="Max session lifetime sent to IWM on Start — separate from idle timeout"
      >
        <InputNumber min={0} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item
        label="IWM Profile"
        extra="Comes from the selected Package (Workspace Definitions → Packages) unless overridden below."
      >
        {inheritedProfile ? (
          <Typography.Text code>{inheritedProfile}</Typography.Text>
        ) : watchedPackageId ? (
          <Typography.Text type="warning">
            Package has no IWM Profile — set it on Workspace Definitions → Packages
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">Select a Package to inherit its IWM Profile</Typography.Text>
        )}
      </Form.Item>
      <Form.Item
        name="profileId"
        label="Override IWM Profile (optional)"
        extra="Leave blank to use the Package's profile. Only needed if this group must differ."
      >
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="Leave blank to inherit from Package"
          options={iwmProfiles.map((p) => ({
            value: p.id,
            label: p.displayName && p.displayName !== p.id ? `${p.id} — ${p.displayName}` : p.id,
          }))}
        />
      </Form.Item>
    </Form>
  );

  const membersPanel = showMembersSection ? (
    <div className="flex flex-col h-full min-h-0">
      {setupMode === 'individual' && (
        <Alert
          className="mb-3 shrink-0"
          type={members.length > 0 ? 'success' : 'warning'}
          showIcon
          message={members.length > 0 ? `${members.length} member(s) linked` : 'Add at least one member for this individual allocation.'}
        />
      )}
      <div className="mb-2 shrink-0 sticky top-0 z-10 bg-white pb-2">
        <Input
          allowClear
          placeholder="Filter members by User ID or email (e.g. b23o_)"
          value={memberFilter}
          onChange={(e) => setMemberFilter(e.target.value)}
        />
        <Typography.Text type="secondary" className="text-xs">
          Showing {filteredMembers.length} of {members.length}
          {memberFilter.trim() ? ` matching “${memberFilter.trim()}”` : ''}
        </Typography.Text>
        {canWrite && (
          <div className="mt-2 flex justify-between gap-2 flex-wrap">
            <div>
              {memberSelectedKeys.length > 0 && (
                <Button
                  danger
                  size="small"
                  loading={removingMembers}
                  onClick={handleBulkRemoveMembers}
                >
                  Remove selected ({memberSelectedKeys.length})
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="default"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => setAttachExistingOpen(true)}
              >
                Add existing Unique Users…
              </Button>
              <Button
                type={setupMode === 'individual' ? 'primary' : 'default'}
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  memberForm.resetFields();
                  memberForm.setFieldsValue({ isPrimary: setupMode === 'individual' });
                  setMemberModalOpen(true);
                }}
              >
                Add Member
              </Button>
            </div>
          </div>
        )}
      </div>
      <Table
        className="group-members-table"
        rowKey={(r) => String(r.id ?? r.userId)}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={memberCols as any}
        dataSource={filteredMembers}
        scroll={{ x: 720 }}
        locale={{
          emptyText: memberFilter.trim()
            ? 'No members match this filter'
            : 'No members yet',
        }}
        rowSelection={
          canWrite
            ? {
                selectedRowKeys: memberSelectedKeys,
                onChange: (keys) => setMemberSelectedKeys(keys),
                preserveSelectedRowKeys: true,
              }
            : undefined
        }
      />
      <style>{`
        .group-members-table .ant-checkbox-inner {
          border: 2px solid #5A6474 !important;
          background: #fff !important;
        }
        .group-members-table .ant-checkbox-checked .ant-checkbox-inner {
          background: #0B5FD0 !important;
          border-color: #0B5FD0 !important;
        }
      `}</style>
    </div>
  ) : (
    <Alert
      type="info"
      showIcon
      message="Save the group first"
      description="Members can be managed after the Group Instance exists."
    />
  );

  const listCard = (
    <Card className="shadow min-w-0 h-full flex flex-col overflow-hidden" styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' } }}>
      {useNew ? (
        <PageHeader
          title="Group Instances"
          purpose={pagePurpose}
          actions={
            canWrite ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {newGroupLabel}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex justify-between items-center mb-4 shrink-0">
          <Typography.Title level={4} className="!mb-0">
            Group Instances
          </Typography.Title>
          {canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {newGroupLabel}
            </Button>
          )}
        </div>
      )}

      <div className="mb-3 shrink-0">
        <Segmented
          value={setupMode}
          onChange={(v) => setSetupMode(v as SetupMode)}
          options={[
            { label: 'Batch / Group', value: 'batch' },
            { label: 'Individual', value: 'individual' },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={filteredRows}
          pagination={{ pageSize: drawerOpen ? 10 : 20, showSizeChanger: true }}
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => openEdit(record),
            style: { cursor: 'pointer' },
          })}
          rowClassName={(record) =>
            editing?.id === record.id ? 'ant-table-row-selected' : ''
          }
        />
      </div>
    </Card>
  );

  const detailCard = drawerOpen ? (
    <Card
      className="shadow min-w-0 h-full flex flex-col overflow-hidden"
      styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', paddingTop: 12 } }}
      title={
        <div className="pr-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold leading-tight">{panelTitleName}</span>
            {editing?.status ? <Tag>{editing.status}</Tag> : null}
            {editing ? (
              <Typography.Text type="secondary" className="text-xs">
                {members.length} member{members.length === 1 ? '' : 's'}
              </Typography.Text>
            ) : null}
          </div>
          <Typography.Text type="secondary" className="text-xs">
            {panelSubtitle}
            {editing ? ` · ${pkgLabel(editing.packageId)} · ${pkgProfile(editing.packageId, editing.profileId)}` : ''}
          </Typography.Text>
        </div>
      }
      extra={
        <Space wrap>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={closeEditor}
            aria-label="Close editor"
          />
          {canWrite ? (
            <>
              <Button onClick={closeEditor}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <Button onClick={closeEditor}>Close</Button>
          )}
        </Space>
      }
    >
      <Tabs
        activeKey={detailTab}
        onChange={(k) => setDetailTab(k as DetailTab)}
        className="gi-detail-tabs flex-1 min-h-0 flex flex-col"
        items={[
          {
            key: 'members',
            label: setupMode === 'individual' ? `Member (${members.length})` : `Members (${members.length})`,
            disabled: !showMembersSection,
            children: <div className="gi-tab-scroll">{membersPanel}</div>,
          },
          {
            key: 'settings',
            label: 'Settings',
            children: <div className="gi-tab-scroll">{settingsForm}</div>,
          },
        ]}
      />
      <style>{`
        .gi-detail-tabs {
          display: flex;
          flex-direction: column;
          min-height: 0;
          flex: 1;
        }
        .gi-detail-tabs .ant-tabs-content-holder {
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }
        .gi-detail-tabs .ant-tabs-content,
        .gi-detail-tabs .ant-tabs-tabpane {
          height: 100%;
        }
        .gi-tab-scroll {
          height: 100%;
          overflow: auto;
          padding-right: 4px;
        }
      `}</style>
    </Card>
  ) : null;

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      {!drawerOpen ? (
        listCard
      ) : (
        <div
          ref={splitContainerRef}
          className="flex flex-col"
          style={{ height: 'calc(100vh - 5.5rem)' }}
        >
          <div style={{ height: `${listTopPct}%` }} className="min-h-0 shrink-0 overflow-hidden">
            {listCard}
          </div>
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize list and detail panes. Double-click to reset."
            title="Drag to resize · double-click to reset"
            className="shrink-0 flex items-center justify-center cursor-row-resize select-none group"
            style={{ height: 10 }}
            onPointerDown={onSplitPointerDown}
            onPointerMove={onSplitPointerMove}
            onPointerUp={onSplitPointerUp}
            onPointerCancel={onSplitPointerUp}
            onDoubleClick={resetSplit}
          >
            <div className="w-16 h-1 rounded-full bg-gray-300 group-hover:bg-blue-500 transition-colors" />
          </div>
          <div style={{ height: `calc(${100 - listTopPct}% - 10px)` }} className="min-h-0 overflow-hidden">
            {detailCard}
          </div>
        </div>
      )}

      <Modal
        title="Add Member"
        open={memberModalOpen}
        onOk={handleAddMember}
        confirmLoading={addingMember}
        onCancel={() => setMemberModalOpen(false)}
        okText="Add"
        destroyOnClose
      >
        <Form form={memberForm} layout="vertical">
          <Form.Item name="userId" label="User ID" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder={setupMode === 'individual' ? 'Login id for this allocation' : undefined} />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="isPrimary" label="Primary" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {editing && (
        <AttachExistingUsersModal
          open={attachExistingOpen}
          onClose={() => setAttachExistingOpen(false)}
          allowPickUsers
          lockedGroupInstanceId={editing.id}
          lockedGroupName={editing.name}
          onAttached={async () => {
            try {
              const [full, list] = await Promise.all([
                getGroupInstance(editing.id),
                listGroupInstances(),
              ]);
              setMembers(full.members || []);
              setRows(list);
            } catch {
              /* keep current */
            }
          }}
        />
      )}
    </div>
  );
};

export default GroupInstancesPage;
